import androidx.compose.runtime.*
import org.jetbrains.compose.web.attributes.*
import org.jetbrains.compose.web.dom.*
import kotlinx.coroutines.launch

// Statistiky hráče: nahoře výběr hráče (default přihlášený), pak stejné statistiky jako u týmu
// ale za 1 hráče, a dole seznam jednotlivých her s kompletním zápisem (📝 doplnit/upravit/smazat).

@Composable
fun PlayerStatsSection(token: String, member: SgMemberDto?, profiles: List<SgProfileDto>, readOnly: Boolean = false) {
    val nameById = remember(profiles) { profiles.associate { it.blPlayerId to it.displayName } }
    var games by remember { mutableStateOf<List<StatGame>>(emptyList()) }
    var seasons by remember { mutableStateOf<List<Pair<String, String>>>(emptyList()) }
    val sheets = remember { mutableStateMapOf<String, PlayerSheetDto>() } // sheetKey -> sheet (vybraný hráč)
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }
    var selectedPlayer by remember { mutableStateOf<String?>(null) }
    var reloadSheets by remember { mutableStateOf(0) }

    var enabledSources by remember { mutableStateOf(setOf("league")) }
    var selectedType by remember { mutableStateOf("classic") }
    var selectedSeason by remember { mutableStateOf<String?>(null) }
    var selectedEvent by remember { mutableStateOf<String?>(null) }
    var statsTab by remember { mutableStateOf("overview") }
    var editorGame by remember { mutableStateOf<StatGame?>(null) }
    val scope = rememberCoroutineScope()

    LaunchedEffect(Unit) {
        try {
            val (g, s) = loadStatGames(token)
            games = g; seasons = s
            selectedSeason = s.firstOrNull()?.first
            selectedPlayer = member?.blPlayerId ?: profiles.firstOrNull()?.blPlayerId
        } catch (e: Throwable) {
            error = e.apiMessage()
        } finally {
            loading = false
        }
    }

    LaunchedEffect(selectedPlayer, reloadSheets) {
        val pid = selectedPlayer ?: return@LaunchedEffect
        try {
            val ps = Api.fetchPlayerSheets(token, pid)
            sheets.clear(); ps.forEach { sheets[it.key()] = it }
        } catch (e: Throwable) {
            error = e.apiMessage()
        }
    }

    val seasonYearById = remember(games) { seasonYears(games) }
    val seasonFiltered = games.filter { g ->
        g.playerId == selectedPlayer && g.source in enabledSources && g.gameType == selectedType &&
            g.matchesSeason(selectedSeason, seasonYearById)
    }
    val activeEvent = selectedEvent?.takeIf { id -> seasonFiltered.any { it.eventKey() == id } }
    val filtered = filterStatEvent(seasonFiltered, activeEvent)

    Div(attrs = { classes("card") }) {
        H3 { Text("Statistiky hráče") }
        when {
            loading -> P { Text("Načítám…") }
            error != null -> P { Text("Chyba: $error") }
            else -> {
                Div(attrs = { classes("filter-group") }) {
                    Span(attrs = { classes("filter-label") }) { Text("Hráč") }
                    Select(attrs = { classes("field"); onChange { ev -> ev.value?.let { selectedPlayer = it } } }) {
                        profiles.forEach { p ->
                            Option(p.blPlayerId, attrs = { if (selectedPlayer == p.blPlayerId) attr("selected", "selected") }) { Text(p.displayName) }
                        }
                    }
                }

                StatFilters(enabledSources, { enabledSources = enabledSources.toggle(it) }, selectedType, { selectedType = it }, seasons, selectedSeason, { selectedSeason = it }) {
                    StatEventFilter(seasonFiltered, nameById, activeEvent) { selectedEvent = it }
                }

                if (filtered.isEmpty()) {
                    P(attrs = { classes("muted") }) { Text("Žádné hry pro zvolený filtr (${bowlingTypeLabel(selectedType)}).") }
                    return@Div
                }
                StatsTabs(statsTab, listOf(
                    "overview" to "Přehled", "frames" to "Framy", "form" to "Forma",
                    "sessions" to "Série", "sources" to "Zdroje", "games" to "Hry",
                )) { statsTab = it }

                when (statsTab) {
                    "frames" -> {
                        val allDetails = games.filter { it.playerId == selectedPlayer }.mapNotNull { game ->
                            sheets[game.sheetKey()]?.takeIf { it.gameType == game.gameType && it.blPlayerId == game.playerId }?.let { DetailedGame(game, it) }
                        }
                        FrameStatsView(seasonFiltered, allDetails, nameById, playerProfile = true, selectedEvent = activeEvent)
                    }
                    "form" -> FormStatsPanel(filtered)
                    "sessions" -> SessionComparisonPanel(filtered)
                    "sources" -> {
                        H4 { Text("Porovnání zdrojů") }
                        BarChart(SOURCE_LABELS.mapNotNull { (code, label) ->
                            val gs = filtered.filter { it.source == code }
                            if (gs.isEmpty()) null else Triple(label, round1(gs.map { it.score }.average()), gs.size)
                        })
                    }
                    "games" -> {
                        H4 { Text("Jednotlivé hry") }
                        Div(attrs = { classes("game-list") }) {
                            filtered.sortedWith(compareByDescending<StatGame> { it.date ?: "" }.thenByDescending { it.gameNo }).forEach { g ->
                                val sheet = sheets[g.sheetKey()]
                                Div(attrs = { classes("game-item") }) {
                                    Div(attrs = { classes("game-item-head") }) {
                                        Span(attrs = { classes("game-src-dot"); attr("style", "background:${sourceColor(g.source)}") }) {}
                                        Span(attrs = { classes("game-score") }) { Text("${g.score}") }
                                        Span(attrs = { classes("muted", "game-label") }) { Text(g.label()) }
                                        sheet?.let {
                                            if (it.total != g.score) Span(attrs = { classes("game-sheet-mismatch") }) { Text("zápis ${it.total}") }
                                        }
                                        if (!readOnly) {
                                            Button(attrs = {
                                                classes(*(listOfNotNull("sheet-btn", "sheet-btn-on".takeIf { sheet != null }).toTypedArray()))
                                                attr("title", "Kompletní zápis"); attr("aria-label", "Kompletní zápis")
                                                onClick { editorGame = g }
                                            }) { Text("📝") }
                                        }
                                    }
                                    sheet?.let { ScoresheetStrip(g.gameType, it.frames) }
                                }
                            }
                        }
                    }
                    else -> {
                        Div(attrs = { classes("career-stats") }) {
                            StatPill2("${round1(filtered.map { it.score }.average())}", "průměr")
                            StatPill2("${filtered.size}", "her")
                            StatPill2("${filtered.maxOf { it.score }}", "nejlepší hra")
                            StatPill2("${filtered.minOf { it.score }}", "nejhorší hra")
                        }
                        H4 { Text("Vývoj výkonnosti v čase") }
                        TimeChart(timePoints(filtered), average = filtered.map { it.score }.average())
                    }
                }
            }
        }

        if (!readOnly) editorGame?.let { g ->
            val createdBy = member?.userId ?: decodeJwtClaims(token)?.sub ?: ""
            ScoresheetEditor(
                gameType = g.gameType,
                initialFrames = sheets[g.sheetKey()]?.frames ?: emptyList(),
                photoImportEnabled = g.source == "league" || g.source == "tournament",
                gameLabel = "${nameById[g.playerId] ?: "Hráč"} · hra ${g.gameNo}",
                expectedScore = g.score,
                onSave = { frames, total ->
                    scope.launch {
                        try {
                            Api.saveOneSheet(token, g.source, g.refId, g.playerId, g.gameNo, g.gameType, frames, total, createdBy)
                            reloadSheets++
                        } catch (e: Throwable) { error = "Uložení selhalo: ${e.apiMessage()}" }
                    }
                    editorGame = null
                },
                onClose = { editorGame = null },
                onDelete = {
                    scope.launch {
                        try {
                            Api.deleteSheet(token, g.source, g.refId, g.playerId, g.gameNo)
                            reloadSheets++
                        } catch (e: Throwable) { error = "Smazání selhalo: ${e.apiMessage()}" }
                    }
                    editorGame = null
                },
            )
        }
    }
}
